/**
 * goalNormalizer.ts
 *
 * Shared goal normalization logic used by both dataService.ts and nativeDataService.ts.
 * Converts snake_case backend goal responses into the frontend Goal shape.
 *
 * Extracted to eliminate DRY violation — previously this logic was duplicated across
 * both services with subtle divergences.
 */

import type { Goal, GoalPhase, Milestone } from '../types';
import { parseLocalDate } from '../utils/sharedFormatters';

// ────────────────────────────────────────────────────────
// Backend Response Types
// ────────────────────────────────────────────────────────

/** Raw backend phase shape (snake_case from API). */
export interface BackendPhase {
  title?: string;
  phase_name?: string;
  /**
   * The key the strict framework schema actually emits for a phase name
   * (services/framework_json_schema.py, required + additionalProperties:
   * false). Was missing from this type, so the normalizer never read it.
   */
  name?: string;
  focus?: string;
  description?: string;
  duration_weeks?: number;
  key_objectives?: string[];
  objectives?: string[];
  key_workouts?: string[];
  volume_progression?: string;
  success_criteria?: string[];
  research_backing?: string;
  target_metrics?: Record<string, unknown>;
  supporting_work_guidance?: string;
}

/** Raw backend milestone shape (snake_case from API). */
export interface BackendMilestone {
  id?: string;
  name?: string;
  title?: string;
  type?: string;
  week?: number;
  target_week?: number;
  target_date?: string;
  description?: string;
  assessment_method?: string;
  success_metrics?: string[];
  success_criteria?: string[];
  isCompleted?: boolean;
  achieved_date?: string;
  status?: string;
}

/** Raw backend goal response shape (snake_case from API). */
export interface BackendGoalResponse {
  id: string;
  title: string;
  description: string;

  // Briefing / synthesis fields
  background_briefing?: string;
  briefing_one_sentence?: string;
  briefing_summary?: string;

  // Framework fields
  has_framework?: boolean;
  framework_id?: string;
  framework_name?: string;
  activity_type?: string;
  category?: string;
  methodology_summary?: string;
  research_context_summary?: string[];
  key_focus_areas?: string[];
  // Read at normalizeBackendGoal (cedarAnalysis.challenges) but never
  // declared, so ts-jest refused to compile this module — which is why it
  // had no test suite. Declared 2026-07-28; no behaviour change.
  challenges?: string[];
  current_phase?: number;
  // 2026-05-05 Thread 7: API surfaces phase_started_date for within-phase
  // progress fraction in calculateUnifiedProgress.
  phase_started_date?: string;

  // Phases & milestones (can arrive as JSON string or array)
  phases?: string | BackendPhase[];
  milestones?: string | BackendMilestone[];

  // Display fields
  icon_type?: string;
  // Icon audit W3 (2026-09-25) — services/goal_display_glyph.py
  display_modality?: string | null;
  activity_icon_type?: string | null;
  timeline?: string;
  target_date?: string;
  event_name?: string;

  // State & metadata
  state?: 'active' | 'completed' | 'paused' | 'archived' | string;
  created_at: string;
  updated_at: string;
}

// ────────────────────────────────────────────────────────
// Citation Stripping (shared utility)
// ────────────────────────────────────────────────────────

/** Strip inline research citations like "Author 2015 [09780e33...]" from text */
export function stripCitations(text: string): string {
  return text
    // Remove "[hexID...]" bracketed IDs
    .replace(/\s*\[[0-9a-f]{6,}\.{0,3}\]/gi, '')
    // Remove "Author Year [hexID...]" patterns
    .replace(/:\s*[A-Z][a-zéèü]+(?:\s+(?:et\s+al\.?|&\s+[A-Z][a-zéèü]+))?\s+\d{4}\s*\[[0-9a-f]{6,}\.{0,3}\](?:,\s*[A-Z][a-zéèü]+(?:\s+(?:et\s+al\.?|&\s+[A-Z][a-zéèü]+))?\s+\d{4}\s*\[[0-9a-f]{6,}\.{0,3}\])*/gi, '')
    // Remove trailing "(Research basis: ...)" parenthetical
    .replace(/\s*\(Research basis:.*?\)\s*\.?/gi, '')
    // Clean up double spaces and trailing punctuation
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+\./g, '.')
    .trim();
}

// ────────────────────────────────────────────────────────
// Phase Normalization
// ────────────────────────────────────────────────────────

export interface NormalizedPhase {
  id: string;
  name: string;
  title: string;
  phase_name?: string;
  phase_order?: number;
  focus: string;
  description: string;
  durationWeeks: number;
  estimatedWeeks: number;
  key_objectives: string[];
  objectives: string[];
  keyWorkouts: string[];
  volumeProgression: string;
  successCriteria: string[];
  researchBacking: string;
  target_metrics?: Record<string, unknown>;
  supporting_work_guidance?: string;
  status: 'active' | 'upcoming' | 'completed';
  startedAt?: Date;
  /**
   * True only for defaultPhases() filler — a generic ladder invented on the
   * client when the backend framework sent no phases. Surfaces MUST NOT
   * present these as the athlete's plan; not the names, and not "Phase 1 of
   * N", whose denominator is invented too.
   */
  isPlaceholder?: boolean;
}

/**
 * Parse and normalize phases from backend response.
 * Handles both JSON string and array input.
 */
export function normalizePhases(
  raw: string | BackendPhase[] | undefined,
  currentPhaseIndex: number,
  createdAt: string,
): NormalizedPhase[] {
  if (!raw) return [];

  let phases: BackendPhase[];
  try {
    phases = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    // Malformed JSON string — fall back to empty rather than crashing all goal loading
    return [];
  }
  if (!Array.isArray(phases)) return [];

  return phases.map((phase, index) => ({
    id: `phase-${index + 1}`,
    // 2026-07-28: `phase.name` added to every fallback chain. The strict
    // framework schema (services/framework_json_schema.py) declares the
    // phase name under the key `name` and is additionalProperties:false,
    // so real LLM-generated phases carry `name` and NOT `phase_name`.
    // Reading only title/phase_name meant every real framework goal
    // normalized to the generic "Phase 1 / Phase 2 / …" — which is why
    // GoalCard's ` · ${currentPhaseName}` suffix never rendered. Legacy
    // rows that do use `phase_name` still win, so nothing regresses.
    name: phase.title || phase.phase_name || phase.name || `Phase ${index + 1}`,
    title: phase.title || phase.phase_name || phase.name || `Phase ${index + 1}`,
    phase_name: phase.phase_name ?? phase.name,
    phase_order: index,
    focus: phase.focus || 'general',
    description: phase.description || '',
    durationWeeks: phase.duration_weeks ?? 4,
    estimatedWeeks: phase.duration_weeks ?? 4,
    key_objectives: phase.key_objectives || [],
    objectives: phase.key_objectives || phase.objectives || [],
    keyWorkouts: phase.key_workouts || [],
    volumeProgression: phase.volume_progression || 'Progressive increase',
    successCriteria: phase.success_criteria || [],
    researchBacking: phase.research_backing || '',
    target_metrics: phase.target_metrics,
    supporting_work_guidance: phase.supporting_work_guidance,
    status:
      index < currentPhaseIndex ? 'completed' as const
      : index === currentPhaseIndex ? 'active' as const
      : 'upcoming' as const,
    startedAt: index <= currentPhaseIndex ? new Date(createdAt) : undefined,
  }));
}

// ────────────────────────────────────────────────────────
// Milestone Normalization
// ────────────────────────────────────────────────────────

export interface NormalizedMilestone {
  id: string;
  type: string;
  title: string;
  name: string;
  week?: number;
  targetWeek: number;
  target_week?: number;
  targetDate?: Date;
  description: string;
  success_metrics: string[];
  success_criteria: string[];
  targetValue: Record<string, unknown>;
  assessmentMethod: string;
  importance: 'high';
  achieved: boolean;
  achievedDate?: Date;
  isCompleted: boolean;
  status?: string;
}

/**
 * Parse and normalize milestones from backend response.
 * Handles both JSON string and array input.
 */
export function normalizeMilestones(
  raw: string | BackendMilestone[] | undefined,
): NormalizedMilestone[] {
  if (!raw) return [];

  let milestones: BackendMilestone[];
  try {
    milestones = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    // Malformed JSON string — fall back to empty rather than crashing all goal loading
    return [];
  }
  if (!Array.isArray(milestones)) return [];

  return milestones.map((ms, index) => ({
    id: ms.id || `milestone-${index + 1}`,
    type: ms.type || 'performance',
    title: ms.name || ms.title || '',
    name: ms.name || ms.title || '',
    week: ms.week,
    targetWeek: ms.week ?? ms.target_week ?? 0,
    target_week: ms.target_week,
    targetDate: ms.target_date ? new Date(ms.target_date) : undefined,
    description: ms.description || ms.assessment_method || '',
    success_metrics: ms.success_metrics || ms.success_criteria || [],
    success_criteria: ms.success_metrics || ms.success_criteria || [],
    targetValue: {},
    assessmentMethod: ms.assessment_method || ms.description || '',
    importance: 'high' as const,
    achieved: ms.isCompleted || ms.status === 'achieved' || ms.status === 'completed' || false,
    achievedDate: ms.achieved_date ? new Date(ms.achieved_date) : undefined,
    isCompleted: ms.isCompleted || ms.status === 'achieved' || ms.status === 'completed' || false,
    status: ms.status,
  }));
}

// ────────────────────────────────────────────────────────
// Default (fallback) Phases
// ────────────────────────────────────────────────────────

// Client-invented filler used when the backend framework sent no phases.
// Every entry is tagged `isPlaceholder` so consumers can tell a real plan
// from this one. Do NOT infer it from field presence — these carry both
// `title` and `name`, so they are indistinguishable from real phases by
// shape alone (a peek drawer briefly shipped a phase_name-based guess and
// it was wrong in both directions).
function defaultPhases(createdAt: string): NormalizedPhase[] {
  return [
    {
      id: 'phase-1',
      isPlaceholder: true,
      title: 'Foundation Building',
      name: 'Foundation Building',
      focus: 'general',
      description: 'Establishing consistent habits and building base fitness',
      durationWeeks: 4,
      estimatedWeeks: 4,
      key_objectives: [],
      objectives: ['Build consistency', 'Establish routine', 'Develop base fitness'],
      keyWorkouts: [],
      volumeProgression: 'Progressive increase',
      successCriteria: ['Complete 75% of planned sessions', 'Feel comfortable with routine'],
      researchBacking: '',
      status: 'active' as const,
      startedAt: new Date(createdAt),
    },
    {
      id: 'phase-2',
      isPlaceholder: true,
      title: 'Progress Development',
      name: 'Progress Development',
      focus: 'general',
      description: 'Building toward your target with progressive challenges',
      durationWeeks: 8,
      estimatedWeeks: 8,
      key_objectives: [],
      objectives: ['Increase intensity', 'Build specific skills', 'Track improvements'],
      keyWorkouts: [],
      volumeProgression: 'Progressive increase',
      successCriteria: ['Meet weekly progression targets', 'Demonstrate skill improvements'],
      researchBacking: '',
      status: 'upcoming' as const,
    },
  ];
}

// ────────────────────────────────────────────────────────
// Full Goal Normalization
// ────────────────────────────────────────────────────────

/**
 * Convert a raw backend goal response into the frontend Goal shape.
 * Used by both dataService and nativeDataService.
 */
export function normalizeBackendGoal(bg: BackendGoalResponse): Goal {
  const currentPhaseIndex = Number(bg.current_phase) || 0;
  const frameworkPhases = normalizePhases(bg.phases, currentPhaseIndex, bg.created_at);
  const keyMilestones = normalizeMilestones(bg.milestones);

  const phases = frameworkPhases.length > 0
    ? frameworkPhases
    : defaultPhases(bg.created_at);

  return {
    id: bg.id,
    title: bg.title,
    description: bg.description,

    // Briefing fields
    backgroundBriefing: bg.background_briefing || undefined,
    briefingOneSentence: bg.briefing_one_sentence || undefined,
    briefingSummary: bg.briefing_summary || undefined,

    // Framework
    hasFramework: bg.has_framework || false,
    frameworkId: bg.framework_id || undefined,
    framework:
      bg.has_framework && bg.framework_id
        ? {
            id: bg.framework_id,
            activityType: bg.activity_type || 'fitness',
            frameworkName: bg.framework_name || 'Custom Framework',
            totalDurationWeeks: frameworkPhases.reduce((sum, p) => sum + (p.durationWeeks || 0), 0),
            methodology_summary: bg.methodology_summary,
            research_context_summary: bg.research_context_summary,
            key_focus_areas: bg.key_focus_areas,
            phases: frameworkPhases as unknown as GoalPhase[],
            milestones: keyMilestones as unknown as Milestone[],
            adaptationRules: {
              milestoneMissThreshold: 0,
              completionRateThreshold: 0,
              feedbackTriggers: [],
              adaptationStrategies: [],
            },
            progressionPattern: 'linear' as const,
            evidenceStrength: 'moderate' as const,
            researchCitations: [],
            userAdaptations: [],
            currentPhase: currentPhaseIndex,
            createdAt: new Date(bg.created_at),
          }
        : undefined,

    // Category & activity type (for label display fallback chain)
    category: bg.category || undefined,
    activity_type: bg.activity_type || undefined,

    // Display
    iconType: (bg.icon_type || 'default') as Goal['iconType'],
    // Served empty (null) stays distinct from not served (undefined): an older
    // snapshot keeps the July glyph chain until the next fetch.
    displayModality: 'display_modality' in bg ? bg.display_modality ?? null : undefined,
    activityIconType: 'activity_icon_type' in bg ? bg.activity_icon_type ?? null : undefined,

    // Cedar analysis
    cedarAnalysis: {
      userContext: bg.background_briefing || 'Goal created from conversation with Coach Cedar',
      challenges: Array.isArray(bg.challenges) ? bg.challenges : (bg.key_focus_areas
        ? (bg.key_focus_areas as string[]).map((a: string) => `Focus on ${String(a).replace(/_/g, ' ')}`)
        : ['Maintaining consistency', 'Balancing with life commitments']),
      approach: bg.methodology_summary || 'Progressive, sustainable approach tailored to your schedule',
      timeline: bg.timeline
        || (bg.target_date && !isNaN(new Date(bg.target_date).getTime())
          ? `Target: ${new Date(bg.target_date).toLocaleDateString()}`
          : ''),
    },

    // Journey (uses current_phase from API)
    // 2026-05-05 (Milestone Spider-Web Doctor Mission Thread 7):
    // Surfaces phase_started_date so dataService.calculateUnifiedProgress can
    // compute within-phase fraction. Pre-Thread-0 this column was NULL on all
    // 38 production frameworks; backfilled by migration 1396.
    journey: {
      phases: phases as unknown as GoalPhase[],
      currentPhase: currentPhaseIndex,
      totalEstimatedWeeks: phases.reduce((sum, p) => sum + (p.estimatedWeeks || 0), 0),
      // @ts-ignore: phaseStartedDate may not yet be in the Goal['journey'] type
      phaseStartedDate: (bg as any).phase_started_date || undefined,
    } as any,

    // Progress
    progress: {
      weeklyPlansCompleted: 0,
      totalWeeksPlanned: 0,
      keyMilestones: keyMilestones as unknown as Milestone[],
    },

    // Motivation
    motivation: {
      whyItMatters: 'Personal fitness and health improvement',
      successVision: 'Achieving your target while maintaining balance with life commitments',
    },

    // Adaptations (nextReview — no backend field, use next Monday as sensible default)
    adaptations: {
      adjustmentsMade: [],
      nextReview: (() => { const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); return d; })(),
    },

    // State & dates
    // Both `status` (web types/index.ts) and `state` (native types/DataTypes.ts) are
    // consumed downstream, so we emit both with consistent defaults.
    // 🩺 AUG 10, 2026 — goal-deactivation-preserves-the-week, symptom S2.
    //
    // This chain used to end in `: 'active' as const`, so EVERY backend
    // state it didn't name explicitly — including 'abandoned' — normalized
    // to 'active'. MyPathScreen partitions goals with
    // `state === 'completed' || 'paused' || 'abandoned' || 'archived'`
    // (MyPathScreen.native.tsx:1489), so an abandoned goal could never
    // reach "Past goals": it rendered under Active Goals forever, labelled
    // Active, with no way for the user to get rid of it.
    //
    // It survived five months (shipped b099ba968, 2026-03-10) because
    // 'abandoned' had ZERO rows platform-wide until p.rush12 abandoned the
    // Perth Block Run on 2026-08-10 — the ABANDON_GOAL terminal state had
    // simply never landed in production before. 'approaching_target' and
    // 'draft' are still at zero rows, so their arms are latent today; they
    // are mapped explicitly anyway so the next state to go live can't
    // reopen this hole.
    //
    // `state` now carries the backend value faithfully (the full
    // migration-101 vocabulary). `status` stays inside its narrower legacy
    // web union — 'abandoned' and 'approaching_target' fold to the nearest
    // legacy bucket rather than widening a type that web surfaces switch
    // on exhaustively. Native reads `state`; the web prototype reads
    // `status`.
    ...(() => {
      const state =
        bg.state === 'active' ? 'active' as const
        : bg.state === 'in_progress' ? 'in_progress' as const
        : bg.state === 'approaching_target' ? 'approaching_target' as const
        : bg.state === 'completed' ? 'completed' as const
        : bg.state === 'paused' ? 'paused' as const
        : bg.state === 'abandoned' ? 'abandoned' as const
        : bg.state === 'archived' ? 'archived' as const
        : null;
      // 'draft' is deliberately absent: GET /api/goals excludes it on BOTH
      // branches (`state=all` → `AND g.state != 'draft'`; the default →
      // `state = 'active'`), so a draft goal cannot reach a client. If one
      // ever does, the warn path below makes it visible rather than
      // silently rendering it as an active goal.

      if (state === null && bg.state) {
        // Genuinely unknown value — the backend vocabulary grew and this
        // mapper did not. Fall back to 'active' (pre-sprint behaviour) but
        // say so, instead of failing silently for another five months.
        console.warn(
          `[goalNormalizer] unmapped backend goal state "${bg.state}" ` +
          `(goal ${bg.id ?? 'unknown'}) — defaulting to active`
        );
      }

      const resolved = state ?? ('active' as const);
      const legacyStatus =
        resolved === 'in_progress' || resolved === 'approaching_target'
          ? ('active' as const)
          : resolved === 'abandoned'
            ? ('archived' as const)
            : resolved;

      return { status: legacyStatus, state: resolved };
    })(),
    createdAt: bg.created_at && !isNaN(new Date(bg.created_at).getTime()) ? new Date(bg.created_at) : new Date(),
    updatedAt: bg.updated_at && !isNaN(new Date(bg.updated_at).getTime()) ? new Date(bg.updated_at) : new Date(),
    // parseLocalDate, NOT new Date(): `target_date` is a date-only value
    // ("2026-11-15"), and `new Date("2026-11-15")` anchors it at UTC
    // midnight — which is the PREVIOUS day everywhere west of UTC. Any
    // surface that then reads local date components renders the goal's
    // target a day early (the peek drawer showed "Sat, Nov 14" for a
    // target of Sunday Nov 15). parseLocalDate builds it at LOCAL
    // midnight, so the calendar day survives.
    targetDate: bg.target_date ? parseLocalDate(bg.target_date) : undefined,
    eventName: bg.event_name,
    timeline: bg.timeline,
  } as Goal;
}
